import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
	Box,
	Button,
	CircularProgress,
	Divider,
	Typography
} from '@mui/material';
import IconNewReleases from '@/images/icon-new-releases.svg';

import { TRIAL_DAYS } from '@/config';
import { useShallowEqualSelector, useTrialDiscount } from '@/hooks';

import { formatPrice } from '@/utils/money';

import ImagePreview from '@/images/magic-remove.webp';
import routes from '@/routes';
import {
	AUTH_MODAL_TYPES,
	setAuthModalRedirect,
	setAuthModalType
} from '@/store/auth';
import { useMedia } from '@/hooks/responsive';
import { setPrice } from '@/store/checkout';
import { useNavigate } from 'react-router-dom';

import { CircleCheck } from '../Icons';

function PlanCardUnified({ sx, plansData, isBilling, isUnsubscribe }) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const trialDiscount = useTrialDiscount();
	const mdDown = useMedia('mdDown');

	const { loggedIn } = useShallowEqualSelector(({ auth }) => ({
		loggedIn: auth.loggedIn
	}));

	const isTrialDiscountVisible = isBilling && trialDiscount;

	const { loading, monthlyPlan, trialPlan } = plansData;

	const features = t('plans.unified.features', {
		returnObjects: true
	});

	const monthlyPrice = formatPrice({
		amount: monthlyPlan?.baseAmount,
		currency: monthlyPlan?.currency
	});

	const trialPrice = formatPrice({
		amount: isTrialDiscountVisible ? 0 : trialPlan?.baseAmount,
		currency: trialPlan?.currency
	});

	const handleClick = () => {
		dispatch(setPrice({ id: trialPlan?.handlerId }));

		if (loggedIn) {
			return navigate(routes.checkout);
		}

		dispatch(setAuthModalType(AUTH_MODAL_TYPES.SIGN_UP));
		dispatch(setAuthModalRedirect(routes.checkout));
	};

	if (loading) {
		return (
			<Box
				sx={{
					position: 'relative',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					width: '100%',
					height: 400
				}}
			>
				<CircularProgress size={40} />
			</Box>
		);
	}

	return (
		<Box
			sx={{
				width: '100%',
				maxWidth: isBilling ? '100%' : 814,
				mx: 'auto',
				display: 'flex',
				bgcolor: 'white',
				border: '1px solid',
				borderColor: isTrialDiscountVisible ? 'primary.main' : 'divider',
				borderRadius: isBilling ? { xs: '4px', md: '24px' } : '12px',
				boxShadow: isBilling ? 'none' : '0 4px 8px 0 rgba(0, 0, 0, 0.30)',
				px: {
					xs: '16px',
					md: '20px'
				},
				py: '24px',
				paddingInlineEnd: {
					md: '36px'
				},
				...sx
			}}
		>
			<Box
				sx={{
					display: {
						xs: 'none',
						md: 'flex'
					},
					alignItems: 'center',
					justifyContent: 'center',
					width: 328,
					marginInlineEnd: '36px',
					flexShrink: 0,
					flexGrow: 1,
					bgcolor: 'neutral.150',
					borderRadius: '12px'
				}}
			>
				<Box
					component="img"
					alt="Preview"
					src={ImagePreview}
					width={278}
					height={262}
					sx={{
						height: 'auto',
						maxWidth: '100%',
						flexShrink: 0,
						objectFit: 'contain'
					}}
				/>
			</Box>

			<Box
				sx={{
					display: 'flex',
					flexDirection: {
						xs: 'column'
					},
					width: '100%'
				}}
			>
				<Box>
					<Typography
						variant="body2"
						sx={{
							mb: '4px',
							fontWeight: isBilling ? 600 : 800
						}}
					>
						{t('plans.unified.title')}
					</Typography>

					<Typography
						variant={mdDown ? 'body1' : 'body2'}
						sx={{
							mb: '28px',
							color: 'text.primary'
						}}
					>
						{t('plans.unified.subtitle')}
					</Typography>

					<Box
						sx={{
							mb: '4px',
							display: 'flex',
							alignItems: 'flex-end',
							gap: '8px'
						}}
					>
						<Typography variant="h2">{monthlyPrice}</Typography>

						<Typography
							variant="body2"
							sx={{
								pb: '8px',
								color: 'text.primary'
							}}
						>
							{t('plans.unified.periodicity')}
						</Typography>
					</Box>

					<Typography
						variant="body0"
						sx={{
							color: 'text.secondary',
							fontWeight: 500,
							display: 'inline-block',

							...(isTrialDiscountVisible
								? {
										mb: '16px'
									}
								: {
										mb: isUnsubscribe ? 0 : '28px'
									})
						}}
					>
						<Trans
							i18nKey="plans.unified.description"
							components={{
								color: (
									<Box
										component="span"
										sx={{
											color: isTrialDiscountVisible
												? 'primary.main'
												: 'inherit',
											fontWeight: isTrialDiscountVisible
												? 700
												: 'inherit'
										}}
									/>
								)
							}}
							values={{ trialDays: TRIAL_DAYS, trialPrice }}
						/>
					</Typography>

					{isTrialDiscountVisible && (
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: {
									xs: 'center',
									md: 'flex-start'
								},
								gap: '8px',
								width: 'fit-content',
								mb: '16px',
								py: '4px',
								px: '8px',
								borderRadius: '4px',
								bgcolor: 'lime',
								color: 'text.primary',
								fontWeight: 700
							}}
						>
							<img
								src={IconNewReleases}
								alt=""
								aria-hidden
								width={20}
								height={20}
							/>
							<Typography variant="body2" sx={{ fontWeight: 600 }}>
								{t('fastCheckout.trialDiscount')}
							</Typography>
						</Box>
					)}

					<Button
						variant="contained"
						color="primary"
						fullWidth
						onClick={handleClick}
						sx={{
							display: isUnsubscribe ? 'none' : 'inline-flex'
						}}
					>
						{t('plans.unified.button', { trialPrice })}
					</Button>
				</Box>

				<Divider
					sx={{
						my: {
							xs: isBilling ? '16px' : '20px',
							md: isBilling ? '20px' : '28px'
						},
						width: {
							xs: '100%'
						},
						height: {
							xs: '1px'
						},
						bgcolor: 'divider'
					}}
				/>

				<Box>
					<Typography
						variant={mdDown && isBilling ? 'body0' : 'body1'}
						sx={{
							mb: { xs: '12px' },
							fontWeight: { xs: 700 }
						}}
					>
						{t('plans.unified.featuresTitle')}
					</Typography>

					<Box
						component="ul"
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 1,
							m: 0,
							p: 0,
							listStyle: 'none'
						}}
					>
						{features.map((feature, index) => (
							<Box
								component="li"
								key={`PlanCardUnified--${index}`}
								sx={{
									display: 'flex',
									alignItems: {
										xs: 'flex-start',
										md: 'center'
									},
									gap: '8px',
									color: 'text.secondary'
								}}
							>
								<CircleCheck size={20} sx={{ color: 'text.primary' }} />

								<Typography
									variant={mdDown ? 'body0' : 'body1'}
									sx={{
										fontWeight: isBilling ? 400 : 500
									}}
									color="text.secondary"
								>
									{feature}
								</Typography>
							</Box>
						))}
					</Box>
				</Box>
			</Box>
		</Box>
	);
}

export default PlanCardUnified;
